import { ArrowRight, ChevronDown, MessageSquareText, Send } from 'lucide-react'
import { FormEvent, KeyboardEvent, useState } from 'react'
import { Link, useLocation } from 'wouter'
import { MusicPlayer } from '../components/MusicPlayer'
import { musicTracks } from '../data/music'
import { useI18n } from '../i18n'

type PageCopy = {
  primaryName: string
  alias: string
  legalName: string
  eyebrow: string
  intro: string
  interviewLabel: string
  interviewTitle: string
  interviewIntro: string
  interviewPlaceholder: string
  interviewPrompts: string[]
  enterInterview: string
  profile: string
  role: string
  focus: string[]
  focusLabel: string
  archive: string
  archiveLinks: Array<{ label: string; href: string }>
  current: string
  currentValue: string
  elsewhere: string
  viewResume: string
  interview: string
  scroll: string
}

const pageCopy: Record<'en' | 'zh-CN', PageCopy> = {
  en: {
    primaryName: 'jzjzzzzzzz',
    alias: '(/梦蝶)',
    legalName: 'John Zhou · Zhixuan Zhou',
    eyebrow: 'JZ — PERSONAL NOTEBOOK',
    intro: 'Engineering, research, and the questions behind the work.',
    interviewLabel: 'Featured',
    interviewTitle: 'Featured interview',
    interviewIntro: 'Ask the portfolio directly. Start with a decision, a failed attempt, or the trade-off behind a system.',
    interviewPlaceholder: 'What would you like to ask first?',
    interviewPrompts: [
      'How do you define a good problem?',
      'Which part of your engineering and research work has challenged you most?',
      'What problem do you want to solve over the next five years?',
    ],
    enterInterview: 'Enter the full interview',
    profile: 'Profile',
    role: 'Student engineer and researcher',
    focus: ['AI agents', 'Robotics', 'Environmental data', 'Music technology'],
    focusLabel: 'Focus',
    archive: 'Archive',
    archiveLinks: [
      { label: 'Resume', href: '/resume' },
      { label: 'Interviews', href: '/chat' },
      { label: 'Knowledge graph', href: '/graph' },
      { label: 'Technical appendix', href: '/evaluation' },
      { label: 'Contact', href: '/contact' },
    ],
    current: 'Current',
    currentValue: 'Building Agent-Me · active',
    elsewhere: 'Elsewhere',
    viewResume: 'View complete resume',
    interview: 'Start an interview',
    scroll: 'Scroll to the featured interview',
  },
  'zh-CN': {
    primaryName: '划水的周某',
    alias: '(/梦蝶)',
    legalName: '周郅轩 · John Zhou',
    eyebrow: 'JZ — PERSONAL NOTEBOOK',
    intro: '记录工程、研究，以及作品背后的问题。',
    interviewLabel: '精选',
    interviewTitle: '精选访谈',
    interviewIntro: '直接向作品集提问。从一次决策、一次失败，或一个系统背后的取舍开始。',
    interviewPlaceholder: '你想从哪个问题开始？',
    interviewPrompts: [
      '你如何定义一个「好问题」？',
      '工程与研究中，最让你着迷的部分是什么？',
      '未来五年，你想解决什么问题？',
    ],
    enterInterview: '进入完整访谈',
    profile: '个人档案',
    role: '学生工程师与研究者',
    focus: ['AI 智能体', '机器人', '环境数据', '音乐技术'],
    focusLabel: '聚焦',
    archive: '档案',
    archiveLinks: [
      { label: '履历', href: '/resume' },
      { label: '访谈', href: '/chat' },
      { label: '知识图谱', href: '/graph' },
      { label: '技术附录', href: '/evaluation' },
      { label: '联系方式', href: '/contact' },
    ],
    current: '近况',
    currentValue: '正在开发 Agent-Me · 持续更新',
    elsewhere: '其他地方',
    viewResume: '查看完整履历',
    interview: '开始访谈',
    scroll: '向下查看精选访谈',
  },
}

function RailHeading({ children, suffix, id }: { children: string; suffix?: string; id?: string }) {
  return (
    <header className="flex items-baseline justify-between border-b border-ink/18 pb-3">
      <h2 id={id} className="text-[10px] font-semibold uppercase tracking-[0.16em] text-ink/72">{children}</h2>
      {suffix && <span className="font-mono text-[9px] uppercase tracking-[0.14em] text-ink/65">{suffix}</span>}
    </header>
  )
}

function CompactContact() {
  return (
    <address className="mt-4 not-italic" data-testid="contact-details">
      <a aria-label="Email johnzhou811@gmail.com" href="mailto:johnzhou811@gmail.com" className="block border-b border-ink/14 py-2.5 text-[10px] leading-4 text-ink/72 hover:text-moss">johnzhou811@gmail.com</a>
      <a aria-label="GitHub github.com/jzjzzzzzzz" href="https://github.com/jzjzzzzzzz" target="_blank" rel="noreferrer noopener" className="block border-b border-ink/14 py-2.5 text-[10px] leading-4 text-ink/72 hover:text-moss">github.com/jzjzzzzzzz</a>
      <span className="block border-b border-ink/14 py-2.5 text-[10px] text-ink/72">WeChat · Johnzhou2010</span>
    </address>
  )
}

export function LandingPage() {
  const { locale } = useI18n()
  const page = pageCopy[locale]
  const [, navigate] = useLocation()
  const [question, setQuestion] = useState('')

  const startInterview = (value: string) => {
    const clean = value.trim()
    if (clean) window.sessionStorage.setItem('agentme-home-question', clean)
    navigate('/chat')
  }

  const submitInterview = (event: FormEvent) => {
    event.preventDefault()
    startInterview(question)
  }

  const interviewKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return
    event.preventDefault()
    if (question.trim()) startInterview(question)
  }

  return (
    <div className="bg-paper text-ink">
      <section id="home" aria-labelledby="identity-heading" className="relative flex min-h-svh scroll-mt-16 overflow-hidden">
        <picture className="portfolio-hero-media absolute inset-y-0 right-0 w-[72%]">
          <source srcSet="/common-use/1.webp" type="image/webp" />
          <img
            src="/common-use/1.png"
            alt={locale === 'zh-CN' ? '蓝粉色花卉与人物插画背景' : 'Pastel blue and pink floral character illustration'}
            width={1448}
            height={1086}
            decoding="async"
            className="portfolio-hero-image size-full object-cover"
          />
        </picture>

        <div className="relative mx-auto flex min-h-svh w-full max-w-[1240px] items-center px-5 pb-24 pt-28 sm:px-8 lg:px-10">
          <div className="max-w-2xl pt-[24vh] sm:pt-0">
            <p className="font-mono text-[9px] uppercase tracking-[0.36em] text-ink/72">{page.eyebrow}</p>
            <h1 id="identity-heading" aria-label={`${page.primaryName} ${page.alias}`} className="mt-7 font-home-zh font-normal leading-[.9] tracking-[-0.045em] text-ink">
              <span className="text-[clamp(3.4rem,8.5vw,7.2rem)]">{page.primaryName}</span>
              <span className="ml-3 inline-block align-baseline font-sans text-[clamp(.9rem,1.4vw,1.15rem)] font-normal tracking-normal text-ink/68">{page.alias}</span>
            </h1>
            <p className="mt-5 text-[10px] font-semibold uppercase tracking-[0.17em] text-ink/72">{page.legalName}</p>
            <p className="mt-8 font-home-zh text-[clamp(1.15rem,2vw,1.55rem)] leading-[1.65] text-ink/88">不知周之梦为胡蝶与，胡蝶之梦为周与？</p>
            <p className="mt-2 text-[10px] tracking-[0.12em] text-ink/68">—《庄子·齐物论》</p>
            <p className="mt-7 max-w-lg text-xs leading-6 text-ink/72">{page.intro}</p>
            <nav aria-label={locale === 'zh-CN' ? '首页快捷入口' : 'Homepage shortcuts'} className="mt-7 flex flex-wrap items-center gap-3 text-[11px] font-semibold">
              <Link href="/chat" className="inline-flex items-center gap-2 rounded-lg bg-ink px-4 py-3 text-white transition hover:-translate-y-0.5 hover:bg-moss">{page.interview}<ArrowRight size={12} /></Link>
              <Link href="/resume" className="rounded-lg border border-ink/28 bg-white/78 px-4 py-3 transition hover:border-moss/55 hover:text-moss">{page.viewResume}</Link>
              <Link href="/human-api" data-testid="home-human-api-link" className="border-b border-moss/55 pb-1 text-moss transition hover:border-ink hover:text-ink">Human API</Link>
            </nav>
          </div>
        </div>

        <a href="#interview" aria-label={page.scroll} className="absolute bottom-7 left-1/2 grid -translate-x-1/2 place-items-center text-ink/72 transition duration-200 hover:translate-y-1 hover:text-ink">
          <ChevronDown size={18} aria-hidden="true" />
        </a>
      </section>

      <section id="interview" aria-labelledby="interview-heading" className="section-reveal scroll-mt-[72px] border-y border-ink/16 bg-paper">
        <div className="mx-auto grid max-w-[1240px] lg:items-start lg:grid-cols-[230px_minmax(0,1fr)_260px]">
          <aside className="hidden border-r border-ink/16 px-7 py-14 lg:block lg:h-[calc(100vh-7rem)]">
            <div>
              <div className="hidden lg:block">
                <RailHeading>{page.profile}</RailHeading>
                <img
                  src="/common-use/头像.png"
                  alt={locale === 'zh-CN' ? '划水的周某（周郅轩）的头像' : 'Avatar of John Zhou'}
                  width={72}
                  height={72}
                  loading="lazy"
                  decoding="async"
                  className="mt-6 size-[72px] rounded-full border border-ink/18 object-cover grayscale"
                />
                <p className="mt-5 font-serif text-xl">{page.primaryName} <span className="font-sans text-[10px] text-ink/65">{page.alias}</span></p>
                <p className="mt-2 text-[10px] leading-5 text-ink/68">{page.role}</p>
                <p className="mt-5 border-t border-ink/14 pt-4 font-home-zh text-xs leading-6 text-ink/72">不知周之梦为胡蝶与，<br />胡蝶之梦为周与？</p>

                <div className="mt-6 border-t border-ink/14 pt-4">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-ink/65">{page.focusLabel}</p>
                  <p className="mt-2 text-[10px] leading-5 text-ink/68">{page.focus.join(' · ')}</p>
                </div>

                <div className="mt-6 border-t border-ink/14 pt-4">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-ink/65">{page.elsewhere}</p>
                  <CompactContact />
                </div>
              </div>
            </div>
          </aside>

          <div className="min-w-0 px-5 py-12 sm:px-8 lg:px-12 lg:py-14">
            <RailHeading id="interview-heading" suffix={page.interviewLabel}>{page.interviewTitle}</RailHeading>
            <div className="mt-7 flex items-start gap-3">
              <span className="grid size-9 shrink-0 place-items-center rounded-lg border border-moss/16 bg-[#edf3f7] text-moss"><MessageSquareText size={15} /></span>
              <div><h3 className="font-serif text-3xl tracking-[-0.03em] text-ink sm:text-4xl">{page.interviewPlaceholder}</h3><p className="mt-3 max-w-2xl text-xs leading-6 text-ink/64">{page.interviewIntro}</p></div>
            </div>

            <form onSubmit={submitInterview} className="mt-7 rounded-lg border border-ink/14 bg-white p-2 transition focus-within:border-moss/55 focus-within:shadow-[0_14px_40px_rgba(16,47,80,.08)]">
              <label className="sr-only" htmlFor="home-interview-question">{page.interviewPlaceholder}</label>
              <textarea id="home-interview-question" value={question} onChange={(event) => setQuestion(event.target.value.slice(0, 2000))} onKeyDown={interviewKeyDown} rows={3} placeholder={page.interviewPlaceholder} className="w-full resize-none bg-transparent px-3 py-3 text-sm leading-6 outline-hidden placeholder:text-ink/32" />
              <div className="flex items-center justify-between border-t border-ink/8 px-2 pt-2">
                <span className="font-mono text-[9px] text-ink/38">Enter ↵</span>
                <button type="submit" className="grid size-9 place-items-center rounded-lg bg-ink text-white transition hover:bg-moss disabled:opacity-35" disabled={!question.trim()} aria-label={locale === 'zh-CN' ? '提交访谈问题' : 'Submit interview question'}><Send size={14} /></button>
              </div>
            </form>

            <div className="mt-8">
              <p className="text-[9px] font-semibold uppercase tracking-[0.15em] text-ink/55">{locale === 'zh-CN' ? '试试这些问题' : 'Try these questions'}</p>
              <div className="mt-3 border-t border-ink/14">
                {page.interviewPrompts.map((prompt, index) => (
                  <button key={prompt} type="button" onClick={() => startInterview(prompt)} className="group grid w-full grid-cols-[2rem_minmax(0,1fr)_1.25rem] items-center border-b border-ink/12 py-4 text-left text-[12px] text-ink/72 transition hover:bg-white/70 hover:text-moss">
                    <span className="font-mono text-[9px] text-moss/72">0{index + 1}</span><span>{prompt}</span><ArrowRight size={12} className="transition group-hover:translate-x-1" />
                  </button>
                ))}
              </div>
            </div>
            <button type="button" onClick={() => startInterview('')} className="group mt-8 inline-flex items-center gap-3 border-b border-moss/45 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-moss">
              {page.enterInterview}<ArrowRight size={12} className="transition group-hover:translate-x-1" />
            </button>
          </div>

          <aside className="order-3 self-start overflow-y-auto border-t border-ink/16 px-5 py-10 sm:px-8 lg:h-[calc(100vh-7rem)] lg:border-l lg:border-t-0 lg:px-5 lg:py-14">
            <div className="space-y-9">
              <MusicPlayer tracks={musicTracks} compact />
              <section>
                <RailHeading>{page.archive}</RailHeading>
                <nav aria-label={page.archive} className="mt-3">
                  {page.archiveLinks.map((item) => (
                    <Link key={item.href} href={item.href} className="group flex items-center justify-between border-b border-ink/14 py-2.5 text-[10px] text-ink/72 hover:text-moss">
                      {item.label}<ArrowRight size={10} className="transition duration-200 group-hover:translate-x-1" aria-hidden="true" />
                    </Link>
                  ))}
                </nav>
              </section>

              <section>
                <RailHeading>{page.current}</RailHeading>
                <p className="mt-4 text-[10px] leading-5 text-ink/72">{page.currentValue}</p>
              </section>

            </div>
          </aside>
        </div>
      </section>
    </div>
  )
}
